import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { toast } from "sonner";
import {
  Heart,
  ShoppingCart,
  Star,
  Truck,
  Shield,
  RotateCcw,
  Minus,
  Plus,
  Ruler,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);

  const inWishlist = product ? isInWishlist(product.id) : false;

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setSelectedSize(response.data.sizes[0]);
      setSelectedColor(response.data.colors[0]);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }
    if (!selectedColor) {
      toast.error("Please select a color");
      return;
    }
    const success = await addToCart(product.id, quantity, selectedSize, selectedColor);
    if (success) {
      // Optionally navigate to cart
    }
  };

  const handleWishlist = () => {
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product.id);
    }
  };

  const SizeGuide = () => {
    const isBoys = product?.category === "boys";
    
    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="link" className="text-primary p-0 h-auto" data-testid="size-guide-btn">
            <Ruler className="w-4 h-4 mr-1" />
            Size Guide
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Size Guide</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {isBoys ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 text-left">Age</th>
                    <th className="py-2 text-left">Chest (inches)</th>
                    <th className="py-2 text-left">Length (inches)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b"><td className="py-2">4-5Y</td><td>24-26</td><td>18</td></tr>
                  <tr className="border-b"><td className="py-2">6-7Y</td><td>26-28</td><td>20</td></tr>
                  <tr className="border-b"><td className="py-2">8-9Y</td><td>28-30</td><td>22</td></tr>
                  <tr className="border-b"><td className="py-2">10-11Y</td><td>30-32</td><td>24</td></tr>
                  <tr><td className="py-2">12-13Y</td><td>32-34</td><td>26</td></tr>
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 text-left">Size</th>
                    <th className="py-2 text-left">Chest (inches)</th>
                    <th className="py-2 text-left">Length (inches)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b"><td className="py-2">S</td><td>36-38</td><td>27</td></tr>
                  <tr className="border-b"><td className="py-2">M</td><td>38-40</td><td>28</td></tr>
                  <tr className="border-b"><td className="py-2">L</td><td>40-42</td><td>29</td></tr>
                  <tr className="border-b"><td className="py-2">XL</td><td>42-44</td><td>30</td></tr>
                  <tr><td className="py-2">XXL</td><td>44-46</td><td>31</td></tr>
                </tbody>
              </table>
            )}
            <p className="text-muted-foreground text-sm mt-4">
              * Measurements may vary by ±0.5 inches
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cotton-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div>
            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-white mb-4">
              <img
                src={product.images[activeImage]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={cn(
                      "w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors",
                      activeImage === index ? "border-primary" : "border-transparent"
                    )}
                  >
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium text-white",
                  product.category === "mens" ? "bg-brand-mens" : "bg-brand-boys"
                )}
              >
                {product.brand}
              </span>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-accent fill-accent" />
                <span className="text-sm font-medium">{product.avg_rating.toFixed(1)}</span>
                <span className="text-muted-foreground text-sm">
                  ({product.review_count} reviews)
                </span>
              </div>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4" data-testid="product-name">
              {product.name}
            </h1>

            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              {product.description}
            </p>

            <div className="text-3xl font-bold text-primary mb-6" data-testid="product-price">
              ₹{product.price.toLocaleString("en-IN")}
            </div>

            {/* Size Selection */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">Select Size</h3>
                <SizeGuide />
              </div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={cn(
                      "px-4 py-2 border rounded-lg font-medium transition-colors",
                      selectedSize === size
                        ? "border-primary bg-primary text-white"
                        : "border-border hover:border-primary"
                    )}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">Select Color</h3>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={cn(
                      "px-4 py-2 border rounded-lg font-medium transition-colors",
                      selectedColor === color
                        ? "border-primary bg-primary text-white"
                        : "border-border hover:border-primary"
                    )}
                    data-testid={`color-${color}`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">Quantity</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 hover:bg-muted"
                    data-testid="quantity-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-4 font-medium" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-2 hover:bg-muted"
                    data-testid="quantity-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-muted-foreground text-sm">
                  {product.stock} in stock
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 mb-8">
              <Button
                onClick={handleAddToCart}
                size="lg"
                className="flex-1 bg-primary hover:bg-primary/90 rounded-full"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
              <Button
                onClick={handleWishlist}
                size="lg"
                variant="outline"
                className={cn(
                  "rounded-full",
                  inWishlist && "bg-red-50 border-red-200 text-red-500"
                )}
                data-testid="wishlist-btn"
              >
                <Heart className={cn("w-5 h-5", inWishlist && "fill-current")} />
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-white rounded-lg">
              <div className="text-center">
                <Truck className="w-6 h-6 mx-auto text-primary mb-2" />
                <p className="text-xs text-muted-foreground">Free Shipping</p>
              </div>
              <div className="text-center">
                <Shield className="w-6 h-6 mx-auto text-primary mb-2" />
                <p className="text-xs text-muted-foreground">Quality Assured</p>
              </div>
              <div className="text-center">
                <RotateCcw className="w-6 h-6 mx-auto text-primary mb-2" />
                <p className="text-xs text-muted-foreground">Easy Returns</p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8">
            Customer Reviews
          </h2>

          {reviews.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reviews.map((review) => (
                <div key={review.id} className="bg-white p-6 rounded-lg" data-testid={`review-${review.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={cn(
                            "w-4 h-4",
                            i < review.rating
                              ? "text-accent fill-accent"
                              : "text-gray-300"
                          )}
                        />
                      ))}
                    </div>
                    <span className="font-medium">{review.user_name}</span>
                  </div>
                  <p className="text-foreground">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
